import clsx from 'clsx';

/**
 * The NLinks mark, inline rather than an <img>.
 *
 * Inlining lets the stroke pick up `currentColor` where needed and avoids a
 * second network request for something that appears on the first paint of
 * every screen — including the login page, before anything else has loaded.
 */
export function LogoMark({ className, title = 'NLinks' }: { className?: string; title?: string }) {
  return (
    <svg viewBox="0 0 64 64" className={className} role="img" aria-label={title}>
      <defs>
        <linearGradient id="nlinks-tile" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="64" y2="64">
          <stop offset="0%" stopColor="#0f1c33" />
          <stop offset="100%" stopColor="#070b14" />
        </linearGradient>
        {/*
            userSpaceOnUse, not the default objectBoundingBox: two of the three
            strokes below are perfectly vertical, and a vertical line has a
            zero-width bounding box. Per the SVG spec an element whose bbox is
            empty in one dimension is not rendered with an objectBoundingBox
            gradient — so both uprights of the N silently disappear.
          */}
          <linearGradient id="nlinks-stroke" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="64" y2="64">
          <stop offset="0%" stopColor="#7dd3fc" />
          <stop offset="55%" stopColor="#38bdf8" />
          <stop offset="100%" stopColor="#0284c7" />
        </linearGradient>
      </defs>

      <rect width="64" height="64" rx="15" fill="url(#nlinks-tile)" />
      <rect
        x="0.75"
        y="0.75"
        width="62.5"
        height="62.5"
        rx="14.25"
        fill="none"
        stroke="#38bdf8"
        strokeOpacity="0.22"
        strokeWidth="1.5"
      />

      {/* N — the diagonal is the traversal from mailbox to destination. */}
      <g
        fill="none"
        stroke="url(#nlinks-stroke)"
        strokeWidth="6.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M19 45.5V20" />
        <path d="M19 20l26 24.5" />
        <path d="M45 44.5V19" />
      </g>

      {/* Endpoints: where the link starts, and where it lands. */}
      <circle cx="19" cy="19.5" r="4.75" fill="#070b14" />
      <circle cx="19" cy="19.5" r="3.1" fill="#7dd3fc" />
      <circle cx="45" cy="44.5" r="4.75" fill="#070b14" />
      <circle cx="45" cy="44.5" r="3.1" fill="#34d399" />
    </svg>
  );
}

/** Mark plus wordmark, for the sidebar and the login screen. */
export function Logo({
  className,
  markClassName = 'h-8 w-8',
  showWordmark = true,
  size = 'md',
}: {
  className?: string;
  markClassName?: string;
  showWordmark?: boolean;
  size?: 'md' | 'lg';
}) {
  return (
    <div className={clsx('flex items-center gap-2.5', className)}>
      <LogoMark className={markClassName} />
      {showWordmark ? (
        <span
          className={clsx(
            'font-semibold tracking-tight text-slate-100',
            size === 'lg' ? 'text-2xl' : 'text-base',
          )}
        >
          {/* "N" carries the weight and "Links" is lighter, so the lockup reads
              as one name rather than two parts of equal emphasis. */}
          N<span className="font-normal text-slate-400">Links</span>
        </span>
      ) : null}
    </div>
  );
}
